import SettingsIcon from '@mui/icons-material/Settings';
import { Box, IconButton, ListItem, ListItemText, Switch } from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { siteFeatureRequiresCommandLease } from '@sophocles/features/entities/site';
import {
  bulkFeatureAction,
  createFeatureAssociation,
  editFeatureAssociation,
} from '@sophocles/features/featureFlags/api';
import { selectFeatureAssociationByEntityAndKey } from '@sophocles/features/featureFlags/slice';
import useSiteCommandWrite from '@sophocles/hooks/useSiteCommandWrite';

import FeatureSettingsDialog from './FeatureSettingsDialog';

/**
 * Determines if an entity type has children that features can be applied to.
 * - customer -> has sitegroups as children
 * - sitegroup -> has sites as children
 * - site -> no children
 */
const entityHasChildren = entityType => entityType === 'customer' || entityType === 'sitegroup';

function FeatureCard({ feature, entityType, entityId, disabled = false }) {
  const dispatch = useDispatch();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const association = useSelector(selectFeatureAssociationByEntityAndKey(entityType, entityId, feature.key));
  const siteId = entityType === 'site' ? entityId : null;
  const { commandLocked, runWrite } = useSiteCommandWrite(siteId);
  const nextEnabled = association == null || !association.is_enabled;
  const requiresCommand = entityType === 'site' && siteFeatureRequiresCommandLease(feature.key, nextEnabled);

  const runAssociationWrite = async action => {
    setIsUpdating(true);
    try {
      await runWrite(() => dispatch(action).unwrap(), { requiresCommand });
    } catch {
      // Feature thunks retain their existing rejected-state handling.
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEnable = () =>
    runAssociationWrite(
      association == null
        ? createFeatureAssociation({
            entity_id: entityId,
            entity_type: entityType,
            feature_key: feature.key,
          })
        : editFeatureAssociation({ id: association.id, is_enabled: true }),
    );

  const handleDisable = () => {
    runAssociationWrite(editFeatureAssociation({ id: association.id, is_enabled: false }));
  };

  const handleUpdateFeature = data => dispatch(editFeatureAssociation(data));

  const handleBulkAction = data => dispatch(bulkFeatureAction(data));

  if (association == null) {
    return (
      <ListItem divider sx={{ width: '100%' }}>
        <ListItemText primary={feature.name} secondary={feature.description} />
        <Box display="flex" alignItems="center" gap={2} ml="auto">
          <Switch
            checked={false}
            onChange={handleEnable}
            disabled={disabled || isUpdating || (requiresCommand && commandLocked)}
            color="primary"
          />
        </Box>
      </ListItem>
    );
  }

  const supportsSettings = association?.feature?.supports_default_for_children && entityHasChildren(entityType);

  return (
    <>
      <ListItem divider sx={{ width: '100%' }}>
        <ListItemText primary={feature.name} secondary={feature.description} />
        <Box display="flex" alignItems="center" gap={2} ml="auto">
          {supportsSettings && (
            <IconButton
              onClick={() => setSettingsOpen(true)}
              disabled={disabled || !association?.is_effectively_enabled}
              aria-label="Configure feature settings"
            >
              <SettingsIcon />
            </IconButton>
          )}
          <Switch
            disabled={
              association?.is_disabled_from_parent || disabled || isUpdating || (requiresCommand && commandLocked)
            }
            checked={association?.is_effectively_enabled}
            onChange={() => {
              if (association?.is_enabled) {
                handleDisable();
              } else {
                handleEnable();
              }
            }}
            color="primary"
          />
        </Box>
      </ListItem>

      {supportsSettings && (
        <FeatureSettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          association={association}
          entityType={entityType}
          entityId={entityId}
          onUpdateFeature={handleUpdateFeature}
          onBulkAction={handleBulkAction}
          disabled={disabled}
        />
      )}
    </>
  );
}

export default FeatureCard;
